/**
 * ゲスト（localStorage）→ ログイン時のマージ（EP-1）。
 *
 * 規則は設計書どおり:
 *   - 位置系（再開位置）= 更新時刻の**新しい方**が勝つ
 *   - 集合系（しおり・ふせん・本棚）= **和集合**
 *   - 削除記録（tombstone）は持たない代わりに、**マージは初回ログイン時の 1 回だけ**
 *
 * 「初回だけ」にすることで「ゲストのときに消したしおりが、ログインのたびに復活する」問題を避ける。
 * 送信そのものはサーバ側が冪等（集合系は UPSERT・位置系は新しい方優先）なので、ここでは
 * 「何を送るか」だけを純粋に決める。
 */
import type { LocalSnapshot, MarkType, ProgressEntry, UserItem } from './store';
import { MARK_TYPES, sameItem } from './store';

/** マージで送る内容。 */
export interface MergePlan {
  marks: { type: MarkType; items: UserItem[] }[];
  progress: ProgressEntry[];
}

/**
 * 初回ログイン時に送るべき内容を決める。送るものが無い（既にマージ済み・データ無し）なら null。
 */
export function planMerge(snapshot: LocalSnapshot): MergePlan | null {
  if (snapshot.mergedAt !== undefined) return null;

  const marks = MARK_TYPES.map((type) => ({ type, items: snapshot[type] })).filter(
    (entry) => entry.items.length > 0,
  );
  const progress = Object.values(snapshot.progress);
  if (marks.length === 0 && progress.length === 0) return null;

  return { marks, progress };
}

/** マージ済みの印を付けた新しいスナップショット（元は変更しない）。 */
export function markMerged(snapshot: LocalSnapshot, at: number): LocalSnapshot {
  return { ...snapshot, mergedAt: at };
}

/** 集合系の追加・更新（同じブック×ページは 1 つに保つ）。 */
export function upsertItem(items: readonly UserItem[], item: UserItem): UserItem[] {
  const rest = items.filter((existing) => !sameItem(existing, item.book, item.page));
  return [...rest, item];
}

/** 集合系の削除。 */
export function removeItem(items: readonly UserItem[], book: string, page?: number): UserItem[] {
  return items.filter((existing) => !sameItem(existing, book, page));
}

/** 位置系の突き合わせ: 更新時刻の新しい方を残す。 */
export function newerProgress(
  current: ProgressEntry | undefined,
  incoming: ProgressEntry,
): ProgressEntry {
  if (!current) return incoming;
  return incoming.updatedAt >= current.updatedAt ? incoming : current;
}
